import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BasemapService,
  type BasemapFormData,
} from "@/services/basemapService";
import type { BasemapConfig } from "@/types/layers";
import { basemaps as fallbackBasemaps } from "@/constant/layers";
import { useTenant } from "./useTenant";

export function useBasemaps() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();

  const BASEMAPS_QUERY_KEY = ["basemaps", tenantId || ""] as const;

  // Fetch all basemaps
  const {
    data: basemaps = fallbackBasemaps,
    isLoading,
    error,
  } = useQuery<BasemapConfig[]>({
    queryKey: BASEMAPS_QUERY_KEY,
    queryFn: async () => {
      try {
        return await BasemapService.getAllBasemaps(tenantId!);
      } catch (err) {
        console.error("Failed to fetch basemaps, using fallback:", err);
        // Return fallback basemaps if fetch fails
        return fallbackBasemaps;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!tenantId,
  });

  // Create basemap mutation
  const createMutation = useMutation({
    mutationFn: (basemapData: BasemapFormData) =>
      BasemapService.createBasemap(basemapData, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BASEMAPS_QUERY_KEY });
    },
  });

  // Update basemap mutation
  const updateMutation = useMutation({
    mutationFn: ({
      key,
      data,
    }: {
      key: string;
      data: Partial<BasemapFormData>;
    }) => BasemapService.updateBasemap(key, data, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BASEMAPS_QUERY_KEY });
    },
  });

  // Delete basemap mutation
  const deleteMutation = useMutation({
    mutationFn: (key: string) => BasemapService.deleteBasemap(key, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BASEMAPS_QUERY_KEY });
    },
  });

  // Update visibility mutation
  const updateVisibilityMutation = useMutation({
    mutationFn: ({ key, isVisible }: { key: string; isVisible: boolean }) =>
      BasemapService.updateBasemapVisibility(key, isVisible, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BASEMAPS_QUERY_KEY });
    },
  });

  // Reorder basemaps mutation with optimistic update
  const reorderMutation = useMutation({
    mutationFn: (updates: Array<{ key: string; display_order: number }>) =>
      BasemapService.reorderBasemaps(updates, tenantId!),
    onMutate: async (updates) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: BASEMAPS_QUERY_KEY });

      // Snapshot the previous value
      const previousBasemaps =
        queryClient.getQueryData<BasemapConfig[]>(BASEMAPS_QUERY_KEY);

      // Optimistically update to the new value
      if (previousBasemaps) {
        const reorderedBasemaps = previousBasemaps.map((basemap) => {
          const update = updates.find((u) => u.key === basemap.key);
          if (update) {
            return { ...basemap, displayOrder: update.display_order };
          }
          return basemap;
        });
        // Sort by display_order
        reorderedBasemaps.sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          return orderA - orderB;
        });
        queryClient.setQueryData(BASEMAPS_QUERY_KEY, reorderedBasemaps);
      }

      // Return context with the previous value
      return { previousBasemaps };
    },
    onError: (_err, _updates, context) => {
      // If the mutation fails, rollback to the previous value
      if (context?.previousBasemaps) {
        queryClient.setQueryData(BASEMAPS_QUERY_KEY, context.previousBasemaps);
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're in sync
      queryClient.invalidateQueries({ queryKey: BASEMAPS_QUERY_KEY });
    },
  });

  // Get visible basemaps only
  const visibleBasemaps = basemaps.filter((b) => b.isVisible !== false);

  return {
    basemaps,
    visibleBasemaps,
    isLoading,
    error,
    createBasemap: createMutation.mutate,
    updateBasemap: updateMutation.mutate,
    deleteBasemap: deleteMutation.mutate,
    updateVisibility: updateVisibilityMutation.mutate,
    reorderBasemaps: reorderMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    // Expose mutation objects for advanced usage
    createMutation,
    updateMutation,
    deleteMutation,
    updateVisibilityMutation,
    reorderMutation,
  };
}
